import { Fragment, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { PACKS, PACK_LIBELLES } from '../../../shared/business-logic/abonnement.ts';
import type { Pack } from '../../../shared/business-logic/abonnement.ts';

interface Ligne {
  csb_id: string; nom: string; csb_actif: boolean; abonnement_id: string | null; statut: string | null; plan: string | null;
  date_debut: string | null; date_fin_essai: string | null; gratuit_jusqu_au: string | null; date_fin: string | null;
  packs: Array<{ pack: Pack; dateFin: string }>; derniere_activite: string | null; derniere_synchro: string | null;
}
const STATUTS = ['TRIAL', 'ACTIVE', 'PENDING_PAYMENT', 'EXPIRED', 'SUSPENDED', 'CANCELLED'];
const PLANS = ['RCE', 'RCE_CPN', 'RCE_CPN_VACCINATION'];
const fmt = (s: string | null) => (s ? new Date(s).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—');

/** ADMIN : packages, dates de début / fin, statut, expiration, dernière activité et dernière synchronisation. L'expiration ne supprime jamais de données. */
export default function AdminAbonnements() {
  const [lignes, setLignes] = useState<Ligne[]>([]);
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, string>>({});
  const [message, setMessage] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const echec = (e: any) => setErreur(e?.message ?? 'Action impossible');

  async function charger() { try { setLignes(await api.get<Ligne[]>('/admin/abonnements')); } catch (e) { echec(e); } }
  useEffect(() => { void charger(); }, []);
  function ouvrir(l: Ligne) {
    setOuvert(ouvert === l.csb_id ? null : l.csb_id);
    setForm({ statut: l.statut ?? '', plan: l.plan ?? '', dateDebut: l.date_debut ?? '', dateFinEssai: l.date_fin_essai ?? '', gratuitJusquAu: l.gratuit_jusqu_au ?? '', dateFin: l.date_fin ?? '',
      ...Object.fromEntries(PACKS.map((p) => [`pack_${p}`, l.packs.find((x) => x.pack === p)?.dateFin ?? ''])) });
  }
  async function enregistrer(l: Ligne) {
    setErreur(null); setMessage(null);
    try {
      await api.put(`/admin/abonnements/${l.csb_id}`, {
        statut: form.statut || undefined, plan: form.plan || undefined, dateDebut: form.dateDebut || undefined, dateFinEssai: form.dateFinEssai || undefined,
        gratuitJusquAu: form.gratuitJusquAu || null, dateFin: form.dateFin || null,
      });
      for (const p of PACKS) {
        const v = form[`pack_${p}`];
        if (v && v !== l.packs.find((x) => x.pack === p)?.dateFin) await api.put(`/admin/abonnements/${l.csb_id}/packs/${p}`, { dateFin: v });
      }
      setMessage(`Abonnement de ${l.nom} mis à jour.`); await charger();
    } catch (e) { echec(e); }
  }
  const champ = (cle: string, label: string, type = 'date') => (
    <label style={{ display: 'inline-block', marginRight: 12 }}>{label}<br /><input type={type} value={form[cle] ?? ''} onChange={(e) => setForm({ ...form, [cle]: e.target.value })} /></label>
  );
  const aujourdhui = new Date().toISOString().slice(0, 10);

  return (
    <div>
      <h1>Abonnements des CSB</h1>
      <p className="note">À l'expiration, seul le statut change : toutes les données restent conservées et consultables après renouvellement.</p>
      {message && <p role="status">✅ {message}</p>}
      {erreur && <p className="messages-erreur">{erreur}</p>}
      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead><tr><th>CSB</th><th>Statut</th><th>Packs (fin)</th><th>Début</th><th>Fin</th><th>Dernière activité</th><th>Dernière synchro</th><th /></tr></thead>
          <tbody>
            {lignes.map((l) => {
              const fin = [l.date_fin, l.gratuit_jusqu_au, ...l.packs.map((p) => p.dateFin)].filter(Boolean).sort().pop() ?? null;
              const expire = !!fin && fin < aujourdhui;
              return (
                <Fragment key={l.csb_id}>
                  <tr>
                    <td>{l.nom}{l.csb_actif ? '' : ' (inactif)'}</td>
                    <td>{l.statut ?? '—'}{expire && l.statut !== 'EXPIRED' ? ' · expiré' : ''}</td>
                    <td>{l.packs.length ? l.packs.map((p) => `${PACK_LIBELLES[p.pack]} → ${p.dateFin}`).join(' ; ') : '—'}</td>
                    <td>{l.date_debut ?? '—'}</td><td>{fin ?? '—'}</td><td>{fmt(l.derniere_activite)}</td><td>{fmt(l.derniere_synchro)}</td>
                    <td><button className="secondaire" onClick={() => ouvrir(l)}>{ouvert === l.csb_id ? 'Fermer' : 'Gérer'}</button></td>
                  </tr>
                  {ouvert === l.csb_id && (
                    <tr><td colSpan={8}>
                      <label style={{ marginRight: 12 }}>Statut<br /><select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })}>{STATUTS.map((s) => <option key={s}>{s}</option>)}</select></label>
                      <label style={{ marginRight: 12 }}>Palier<br /><select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })}><option value="">—</option>{PLANS.map((s) => <option key={s}>{s}</option>)}</select></label>
                      {champ('dateDebut', 'Début')}{champ('dateFinEssai', "Fin d'essai")}{champ('gratuitJusquAu', 'Mois gratuit jusqu’au')}{champ('dateFin', 'Fin abonnement')}
                      <div style={{ margin: '8px 0' }}>{PACKS.map((p) => <span key={p}>{champ(`pack_${p}`, `Fin ${PACK_LIBELLES[p]}`)}</span>)}</div>
                      <button onClick={() => void enregistrer(l)}>Enregistrer</button>
                    </td></tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
